import React, { createContext, useContext, useMemo } from 'react';
import type { TenantConfig } from '@alterrego/core-config';
import { tokens } from './tokens';

type ColorMap = { [K in keyof typeof tokens.color]: string };
type FontMap = {
  family: string;
  size: typeof tokens.font.size;
  weight: typeof tokens.font.weight;
  lineHeight: typeof tokens.font.lineHeight;
};

type Theme = Omit<typeof tokens, 'color' | 'font'> & {
  color: ColorMap;
  font: FontMap;
  tenant: TenantConfig;
};

const ThemeContext = createContext<Theme | null>(null);

export function ThemeProvider({
  config,
  children,
}: {
  config: TenantConfig;
  children: React.ReactNode;
}) {
  const theme = useMemo<Theme>(() => {
    const tc = config.theme?.colors;
    const tf = config.theme?.typography;

    const color: ColorMap = {
      ...tokens.color,
      // branding (legacy — primary/secondary/dark)
      primary: config.branding.primary,
      primaryDark: config.branding.secondary,
      secondary: config.branding.secondary,
      ink: config.branding.dark,
      // theme.colors overrides (prend le dessus sur branding)
      ...(tc?.primary     && { primary:    tc.primary }),
      ...(tc?.secondary   && { secondary:  tc.secondary, primaryDark: tc.secondary }),
      ...(tc?.accent      && { accent:     tc.accent }),
      ...(tc?.surface     && { surface:    tc.surface, white: tc.surface }),
      ...(tc?.surfaceAlt  && { surfaceAlt: tc.surfaceAlt, grey50: tc.surfaceAlt }),
      ...(tc?.text        && { text:       tc.text, ink: tc.text }),
      ...(tc?.textMuted   && { textMuted:  tc.textMuted }),
      ...(tc?.border      && { border:     tc.border }),
      ...(tc?.success     && { success:    tc.success }),
      ...(tc?.warning     && { warning:    tc.warning }),
      ...(tc?.error       && { error:      tc.error, danger: tc.error }),
    };

    const fontFamily = tf?.fontFamily
      ?? `"${config.branding.fontFamily}", -apple-system, system-ui, sans-serif`;

    const merged: Theme = {
      ...tokens,
      color,
      font: { ...tokens.font, family: fontFamily },
      tenant: config,
    };

    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      // Colors
      root.style.setProperty('--color-primary',     color.primary);
      root.style.setProperty('--color-secondary',   color.secondary);
      root.style.setProperty('--color-accent',      color.accent);
      root.style.setProperty('--color-surface',     color.surface);
      root.style.setProperty('--color-surface-alt', color.surfaceAlt);
      root.style.setProperty('--color-text',        color.text);
      root.style.setProperty('--color-text-muted',  color.textMuted);
      root.style.setProperty('--color-border',      color.border);
      root.style.setProperty('--color-success',     color.success);
      root.style.setProperty('--color-warning',     color.warning);
      root.style.setProperty('--color-error',       color.error);
      root.style.setProperty('--color-info',        color.info);
      // Legacy vars (compat)
      root.style.setProperty('--color-primary-dark', color.primaryDark);
      root.style.setProperty('--color-ink',          color.ink);
      // Typography
      root.style.setProperty('--font-family', fontFamily);
      // Radius
      root.style.setProperty('--radius-sm',   tokens.radius.sm);
      root.style.setProperty('--radius-md',   tokens.radius.md);
      root.style.setProperty('--radius-lg',   tokens.radius.lg);
      root.style.setProperty('--radius-xl',   tokens.radius.xl);
      root.style.setProperty('--radius-full', tokens.radius.full);
      // Shadows
      root.style.setProperty('--shadow-sm', tokens.shadow.sm);
      root.style.setProperty('--shadow-md', tokens.shadow.md);
      root.style.setProperty('--shadow-lg', tokens.shadow.lg);
      // Spacing
      root.style.setProperty('--spacing-xs',  tokens.spacing.xs);
      root.style.setProperty('--spacing-sm',  tokens.spacing.sm);
      root.style.setProperty('--spacing-md',  tokens.spacing.md);
      root.style.setProperty('--spacing-lg',  tokens.spacing.lg);
      root.style.setProperty('--spacing-xl',  tokens.spacing.xl);
    }

    return merged;
  }, [config]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans un <ThemeProvider>');
  return ctx;
}
